import React, { useEffect, useRef, useState } from "react";
import algebra from "algebra.js";
import * as d3 from "d3";
import { useDispatch, useSelector } from "react-redux";

import { byLinkClick } from "../../store/actions";
import { getEquationOfLineFromTwoPoints } from "../../utils";
import { ACTIVE_COLOR, LINK_COLOR, START_COLOR } from "../../constants";
import { LinkDrawingProps } from "./types";
import {
  activeGameItemSelector,
  activeItemSelector,
  endNodeSelector,
  gameAnswersSelector,
  gameGraphSelector,
  graphSelector,
  showLinkDotsSelector,
  showPropertiesSelector,
  startNodeSelector,
} from "../../store/selectors";

const LinkDrawing: React.FC<LinkDrawingProps> = ({
  link,
  color = LINK_COLOR,
  notClickable = false,
  isExpanded = false,
}) => {
  const [line, setLine] = useState<any>(null);
  const [drawing, setDrawing] = useState<any>(null);

  const activeItem = useSelector(
    !isExpanded ? activeItemSelector : activeGameItemSelector
  );
  const showProperties = useSelector(showPropertiesSelector);
  const graph = useSelector(!isExpanded ? graphSelector : gameGraphSelector);
  const showLinkDots = useSelector(showLinkDotsSelector);
  const endNode = useSelector(endNodeSelector);
  const startNode = useSelector(startNodeSelector);
  const answers = useSelector(gameAnswersSelector);

  const dispatch = useDispatch();

  const ref = useRef(null);

  useEffect(() => {
    draw();
  }, [link, line]);

  const onLinkClick = (e: any) => {
    dispatch(byLinkClick(link, graph));
    e.stopPropagation();
  };

  const isActive = (): boolean => {
    const { start, end } = link;
    return (
      !!activeItem &&
      activeItem.type === "LINK" &&
      activeItem.link.start.name === start.name &&
      activeItem.link.end.name === end.name
    );
  };

  const getColor = (): string => {
    const ans = [startNode, ...answers, endNode];

    if (isActive()) {
      return ACTIVE_COLOR;
    } else if (isExpanded) {
      if (
        ans.some(
          (node, index) =>
            node?.name === link.start.name &&
            ans[index + 1]?.name === link.end.name
        )
      ) {
        return START_COLOR;
      } else {
        return color;
      }
    } else {
      return color;
    }
  };

  const currentColor = getColor();

  const getArrow = (): string => {
    return `arrow-${currentColor}`;
  };

  const getLink = () => {
    const { start, end } = link;
    const node = graph.nodes[start.name];
    return node.paths.find((p: any) => p.toNodeName === end.name);
  };

  const moveableProps = () => {
    if (notClickable) return {};

    return {
      onMouseDown: (
        event: React.MouseEvent<SVGLineElement | SVGEllipseElement, MouseEvent>
      ): void => {
        onLinkClick(event);
      },
      cursor: "move",
    };
  };

  const draw = () => {
    const { start, end } = link;
    if (drawing) {
      drawing.remove();
      setDrawing(null);
    }

    setDrawing(
      d3
        .select(ref.current)
        ?.append("line")
        .attr("x1", start.x)
        .attr("y1", start.y)
        .attr("x2", end.x)
        .attr("y2", end.y)
        .attr("stroke", currentColor)
        .attr("stroke-width", 3)
        .attr("marker-end", `url(#${getArrow()})`)
    );
  };

  const renderClickableLine = () => {
    if (notClickable) return null;

    const { start, end } = link;
    return (
      <line
        {...moveableProps()}
        stroke="transparent"
        strokeWidth="8px"
        x1={start.x}
        y1={start.y}
        x2={end.x}
        y2={end.y}
      />
    );
  };

  const renderCircle = () => {
    const { start, end } = link;
    const { gradient } = getEquationOfLineFromTwoPoints(start, end);

    const cx = end.x - (end.x - start.x) / 2;
    const cy = end.y - (end.y - start.y) / 2;

    const renderCircle = (x: number, y: number) => {
      const textX = showLinkDots ? x + 6 : x;
      const textY = showLinkDots ? y + 4 : y;
      const link = getLink();
      return (
        <g>
          {showLinkDots && (
            <ellipse
              {...moveableProps()}
              fill={currentColor}
              cx={x}
              cy={y}
              rx="3px"
              ry="3px"
            />
          )}
          {showProperties && (
            <text x={textX} y={textY} className="small-svg-text">
              {link && link.length} u.
            </text>
          )}
        </g>
      );
    };

    if (start.y === end.y) {
      const x = cx;
      const y = start.x > end.x ? cy - 10 : cy + 10;
      return renderCircle(x, y);
    }

    const k1 = gradient;
    const b2 = cx / k1 + cy;
    const k2 = -1 / k1;

    const eq1 = `((x - (${cx}))^2 + ((${k2}) * x + (${b2}) - (${cy}))^2)^0.5 - 100`;
    const expr = algebra.parse(eq1);
    try {
      const eq = new algebra.Equation(expr, 0);

      const solution = eq.solveFor("x");
      const x = start.x > end.x ? solution[0] : solution[1];
      const y = k2 * x + b2;

      return renderCircle(x, y);
    } catch (error) {
      // console.error(error);
    }
    return null;
  };
  return (
    <g ref={ref} style={{ zIndex: currentColor === START_COLOR ? 10 : 0 }}>
      {renderClickableLine()}
      {renderCircle()}
    </g>
  );
};

export default LinkDrawing;
